import React from "react";

interface TotalAchievementScore { revenueOfHomesClosed: string; overridePercentage: string; bonusPoolAvailableAt100: string; totalMaxAt120: string; }
interface BonusCalculationData { totalAchievementScore: TotalAchievementScore; totalScore: string; percentage: string; bonusPool: string; quarterlyFixedBonusRate: string; totalBonusPayout: string; }
interface BonusCalculationsSPMSubFormProps {
  data: BonusCalculationData; isEditable: boolean;
  onInputChange: (e: React.ChangeEvent<HTMLInputElement>, options?: { type?: 'number' | 'decimal' | 'text'; maxLength?: number; minLength?: number; max?: number; min?: number }) => void;
}

const BonusCalculationsSPMSubForm: React.FC<BonusCalculationsSPMSubFormProps> = ({ data, isEditable, onInputChange }) => {
  const fmt = (v: string) => v && v !== "" && !isNaN(Number(v)) ? parseFloat(v).toLocaleString() : "";

  const refBonusAt100 = data.quarterlyFixedBonusRate ? parseFloat(data.quarterlyFixedBonusRate) : 0;
  const refBonusAt120 = refBonusAt100 * 1.2;

  // Responsive styles
  const containerStyle: React.CSSProperties = {
    display: "grid",
    gridTemplateColumns: "5fr 1fr",
    background: "var(--bg-surface-raised)",
  };

  const leftColumnStyle: React.CSSProperties = {
    display: "grid",
    gridTemplateColumns: "3fr 2fr 2fr 2fr",
    borderRight: "1px solid var(--border-section)",
  };

  const rightColumnStyle: React.CSSProperties = {
    padding: "12px 16px",
    background: "var(--bg-chip)",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
  };

  // Mobile styles as inline style object for media query
  const mobileStyles = `
    @media (max-width: 768px) {
      .spm-bonus-calc-container {
        grid-template-columns: 1fr !important;
      }
      .spm-bonus-calc-left {
        grid-template-columns: 1fr 1fr !important;
        border-right: none !important;
        border-bottom: 1px solid var(--border-section);
      }
      .spm-bonus-calc-right {
        padding: 16px !important;
      }
      .sf-calc-label {
        font-size: 10px !important;
      }
      .spm-bonus-calc-left input {
        font-size: 12px !important;
        padding: 4px 6px !important;
      }
    }
  `;

  return (
    <>
      <style>{mobileStyles}</style>
      <div style={{ borderTop: "1px solid var(--border-section)" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 16px", background: "var(--bg-section-header)", borderBottom: "1px solid var(--border-section)" }}>
          <div style={{ width: 3, height: 18, borderRadius: 2, background: "var(--brand-primary)", flexShrink: 0 }} />
          <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.10em", textTransform: "uppercase" as const, color: "var(--text-secondary)" }}>Bonus Calculation</span>
        </div>

        <div className="spm-bonus-calc-container" style={containerStyle}>
          {/* Left: Total Score / % / Quarterly Fixed Bonus Rate */}
          <div className="spm-bonus-calc-left" style={leftColumnStyle}>

          <div style={{ padding: "10px 12px", textAlign: "center", borderLeft: "1px solid var(--border-section)" }}>
            <div className="sf-calc-label">Quarterly Fixed Bonus Rate</div>
            <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
              <span style={{ fontSize: 13, color: "var(--text-muted)" }}>$</span>
              <input type="text" name="bonusCalculation.quarterlyFixedBonusRate"
                value={fmt(data.quarterlyFixedBonusRate) || ""}
                inputMode="decimal" maxLength={12}
                onChange={e => onInputChange(e, { type: "decimal", maxLength: 12 })}
                disabled={!isEditable} placeholder="0" onFocus={e => e.target.select()}
                className="sf-input" style={{ flex: 1, padding: "5px 8px", textAlign: "right", fontSize: 13 }} />
            </div>
          </div>

          <div style={{ padding: "10px 12px", textAlign: "center" }}>
            <div className="sf-calc-label">Total Score (%)</div>
            <input type="text" readOnly name="bonusCalculation.totalScore" value={data.totalScore}
              className="sf-input-ro" style={{ width: "100%", padding: "5px 8px", textAlign: "right", fontSize: 13 }} />
          </div>
          
          <div style={{ padding: "10px 12px", textAlign: "center" }}>
            <div className="sf-calc-label">Ref. Bonus @ 100%</div>
            <input type="text" readOnly name="bonusCalculation.totalScore" value={'$' + fmt(refBonusAt100.toString()) || "0"}
              className="sf-input-ro" style={{ width: "100%", padding: "5px 8px", textAlign: "right", fontSize: 13 }} />
          </div>

          <div style={{ padding: "10px 12px", textAlign: "center" }}>
            <div className="sf-calc-label">Ref. Bonus @ 120%</div>
            <input type="text" readOnly name="bonusCalculation.totalScore" value={'$' +  fmt(refBonusAt120.toString()) || "0"}
              className="sf-input-ro" style={{ width: "100%", padding: "5px 8px", textAlign: "right", fontSize: 13 }} />
          </div>

          {/* <div style={{ padding: "10px 12px", textAlign: "center", borderLeft: "1px solid var(--border-section)" }}>
            <div className="sf-calc-label">%</div>
            <input type="text" readOnly name="bonusCalculation.percentage" value={data.percentage}
              className="sf-input-ro" style={{ width: "100%", padding: "5px 8px", textAlign: "right", fontSize: 13 }} />
          </div> */}
          
        </div>

        {/* Right: Total Bonus Payout */}
        <div className="spm-bonus-calc-right" style={rightColumnStyle}>
          <div style={{ fontSize: 12, fontWeight: 600, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: 6 }}>Total Bonus Payout</div>
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <span style={{ fontSize: 15, color: "var(--text-muted)" }}>$</span>
            <input type="text" readOnly name="bonusCalculation.totalBonusPayout"
              value={fmt(data.totalBonusPayout) || "0"}
              style={{ background: "transparent", border: "none", outline: "none", fontSize: 18, fontWeight: 700, color: "var(--text-primary)", textAlign: "center", width: 160 }} />
          </div>
        </div>
      </div>
    </div>
    </>
  );
};

export default BonusCalculationsSPMSubForm;